/**
 * MissionTopupCheckoutDialog — soft gate before Enferno subscribe checkout.
 *
 * Shown when the user clicks Subscribe (monthly/annual). No option is
 * pre-selected; they must pick a recurring IronLimb top-up or explicitly
 * skip before continuing to Stripe.
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Heart, Loader2 } from 'lucide-react';

const TOPUP_CHOICES = [
  {
    id: 'research',
    label: 'Fund the research',
    detail: 'Phantom limb syndrome and non-opioid pain studies.',
    monthly: 2,
    annual: 24,
  },
  {
    id: 'sponsor',
    label: 'Give someone access',
    detail: "Cover Enferno for an IronLimb-qualified user who can't afford it.",
    monthly: 2,
    annual: 24,
  },
  {
    id: 'both',
    label: 'Do both',
    detail: 'Fund research and sponsor a member.',
    monthly: 4,
    annual: 48,
  },
  {
    id: 'skip',
    label: 'Subscription only',
    detail: 'Continue with Enferno. You can support IronLimb anytime on the Impact page.',
    skip: true,
  },
];

const MissionTopupCheckoutDialog = ({
  plan,
  onClose,
  onConfirm,
}) => {
  const [choice, setChoice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isAnnual = plan?.id === 'enferno-annual';
  const canContinue = choice != null && !submitting;

  const handleContinue = async () => {
    if (!canContinue) return;
    setSubmitting(true);
    setError('');
    try {
      const missionTopupId = choice === 'skip' ? null : choice;
      await onConfirm(missionTopupId);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Checkout failed. Try again.');
      setSubmitting(false);
    }
  };

  const dialog = (
    <div
      className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="mission-topup-checkout-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg max-h-[92vh] overflow-y-auto bg-enf-surface-deep border border-enf-accent-fg/40 rounded-xl shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-enf-surface-border bg-gradient-to-br from-enf-accent/10 to-transparent">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-enf-accent-fg" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-enf-accent-fg">
                IronLimb Foundation
              </div>
              <div className="font-display text-xl tracking-wide leading-none mt-1 text-enf-text-primary">
                Carry the fire forward.
              </div>
              <a
                href="https://ironlimb.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-[11px] text-enf-accent-fg hover:underline mt-1.5"
                data-testid="mission-topup-learn-more"
              >
                Learn more → ironlimb.com
              </a>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-enf-surface-mid rounded-lg"
            data-testid="mission-topup-close"
          >
            <X className="w-4 h-4 text-enf-text-muted" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-enf-text-secondary">
            Enferno exists because of the IronLimb Foundation — funding research into
            phantom limb syndrome, chronic pain, and the non-opioid treatments that
            could have saved someone we loved.
          </p>
          <p className="text-sm text-enf-text-secondary">
            Every dollar you add here goes 100% to the cause you pick below. Tracking
            always visible in the Impact dashboard.
          </p>

          <fieldset className="space-y-2 border-0 p-0 m-0">
            <legend className="sr-only">IronLimb recurring top-up</legend>
            {TOPUP_CHOICES.map((opt) => {
              const optLabel = opt.skip
                ? `${isAnnual ? 'Annual' : 'Monthly'} Subscription Only`
                : opt.label;
              return (
              <label
                key={opt.id}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  choice === opt.id
                    ? 'border-enf-accent-fg/60 bg-enf-surface-elevated'
                    : 'border-enf-surface-border bg-enf-surface-mid hover:border-enf-accent-fg/30'
                }`}
                data-testid={`mission-topup-modal-${opt.id}`}
              >
                <input
                  type="radio"
                  name="mission-topup-modal"
                  value={opt.id}
                  checked={choice === opt.id}
                  onChange={() => setChoice(opt.id)}
                  className="mt-1 accent-enf-accent"
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-enf-text-primary">{optLabel}</span>
                  <span className="block text-xs text-enf-text-muted mt-0.5 leading-relaxed">{opt.detail}</span>
                  {opt.monthly != null ? (
                    <span className="block text-xs text-enf-accent-fg mt-1 font-medium">
                      {isAnnual ? `+$${opt.annual}/yr` : `+$${opt.monthly}/mo`}
                    </span>
                  ) : (
                    <span className="block text-xs text-enf-text-muted mt-1 font-medium">
                      No top-up
                    </span>
                  )}
                </span>
              </label>
              );
            })}
          </fieldset>

          {error && (
            <p className="text-xs text-red-400" data-testid="mission-topup-error">{error}</p>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2.5 rounded-md text-sm font-medium border border-enf-surface-border text-enf-text-secondary hover:bg-enf-surface-mid transition-colors disabled:opacity-60"
            >
              Back to Plans
            </button>
            <button
              type="button"
              onClick={handleContinue}
              disabled={!canContinue}
              data-testid="mission-topup-continue"
              className="px-4 py-2.5 rounded-md text-sm font-semibold bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? 'Opening checkout…' : 'Continue to checkout'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
};

export default MissionTopupCheckoutDialog;
